///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2026
//                                    RXO Inc.
//                         All Rights Reserved Worldwide
//
// WARNING:  This program (or document) is unpublished, proprietary
// property of RXO Inc. and is to be maintained in strict confidence.
// Unauthorized reproduction, distribution or disclosure of this program
// (or document), or any program (or document) derived from it is
// prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import InputMask from "react-input-mask";

import AutoComplete from "../form-controls/AutoComplete";
import OutlinedInput from "../form-controls/OutlinedInput";
import {
  fscTypes,
  equipmentTypes,
  currencyTypes,
  mode,
  internationalLanesTypes,
  division,
  bidScacType,
  bidSubmissionTypes
} from "../../helpers/types";
import { getFscForShipper } from "../../api/shippers";
import Spinner from "../icons/Spinner";
import disableRFQStatus, {
  getClosedBidReopenBlockedTooltipContent
} from "../../helpers/disableRFQStatus";
import { isSelfPricedTruckload } from "../../helpers/isSelfPricedTruckload";
import messages from "../../helpers/messages";
import Tooltip from "../Tooltip";
import getCurrency from "../../helpers/getCurrency";
import CustomDatePicker from "../form-controls/CustomDatePicker";
import { parseISO } from "date-fns";

function Truckload({
  modeDetails,
  setModeDetails,
  validationData,
  removeError,
  shipper,
  role,
  summary,
  userOrBidDivision,
  bidScac,
  isBidView
}) {
  const [fscPegSpinner, toggleFscPegSpinner] = useState(false);
  const [fscFocused, toggleFscFocused] = useState(false);
  const additionalCurrenciesOption = modeDetails.currencyType
    ? currencyTypes.filter(x => x.value !== modeDetails.currencyType.value)
    : currencyTypes;

  const handleValidation = ({ target }) => {
    if (
      isNaN(parseFloat(target.value, 10)) ||
      parseFloat(target.value, 10) > target.max ||
      parseFloat(target.value, 10) < target.min
    ) {
      setModeDetails(mode.tl, target.name, "");
    } else {
      setModeDetails(mode.tl, target.name, parseFloat(target.value, 10));
    }
  };

  const handleNumberChange = ({ target }) =>
    !target.validity.patternMismatch &&
    setModeDetails(mode.tl, target.name, target.value);

  const commonProps = field => ({
    name: field,
    value: modeDetails[field],
    onFocus: () => !validationData[field].isValid && removeError("tl", field),
    error: !validationData[field].isValid,
    validationMessage: validationData[field].validationMessage
  });

  const getCoyoteFsc = () => {
    if (shipper) {
      toggleFscPegSpinner(true);
      getFscForShipper(shipper.shipperId)
        .then(({ data: fscPeg }) => {
          toggleFscPegSpinner(false);
          if (parseFloat(fscPeg)) {
            setModeDetails(
              mode.tl,
              "fuelSurchargePeg",
              parseFloat(fscPeg).toFixed(2)
            );
            !validationData.fuelSurchargePeg.isValid &&
              removeError("tl", "fuelSurchargePeg");
          }
        })
        .catch(() => toggleFscPegSpinner(false));
    }
  };

  const handleCurrencyChange = value => {
    var additionalCurrencies = modeDetails.additionalCurrencies?.filter(
      x => x.value !== value.value
    );
    setModeDetails(mode.tl, "currencyType", value);
    setModeDetails(
      mode.tl,
      "additionalCurrencies",
      additionalCurrencies?.length ? additionalCurrencies : null
    );
  };

  const today = new Date();
  const twoYearOldDate = new Date().setFullYear(today.getFullYear() - 2);

  const isSelfPriced = isSelfPricedTruckload(summary);

  const handleTrimText = ({ target }) => {
    setModeDetails(
      mode.tl,
      target.name,
      target?.value ? target.value.trim() : ""
    );
  };

  const closedBidReopenTooltip = getClosedBidReopenBlockedTooltipContent(
    modeDetails,
    summary
  );
  const rfqStatusCell = (
    <div data-testid="rfq-status-container" className="grid__cell one-half">
      <AutoComplete
        isDisabled={disableRFQStatus(mode.tl, modeDetails, role, summary)}
        label={messages.rfqStatus.defaultMessage}
        value={modeDetails.currentStatus}
        staticOptions={modeDetails.rfqStatusOptions}
        handleChange={value =>
          setModeDetails(mode.tl, "currentStatus", value)
        }
      />
    </div>
  );

  return (
    <div className="truckload grid section-grid">
      {closedBidReopenTooltip ? (
        <Tooltip content={closedBidReopenTooltip}>{rfqStatusCell}</Tooltip>
      ) : (
        rfqStatusCell
      )}
      <div className="grid__cell one-half">
        <OutlinedInput
          type="text"
          label={messages.numberOfLanes.defaultMessage}
          pattern="^[1-9]\d{0,5}$"
          min="1"
          max="999999"
          onBlur={handleValidation}
          onChange={handleNumberChange}
          {...commonProps("laneCount")}
        />
      </div>
      <div className="grid__cell one-half">
        <OutlinedInput
          type="text"
          label={messages.volume.defaultMessage}
          pattern="^[1-9]\d{0,8}$"
          min="1"
          max="999999999"
          onBlur={handleValidation}
          onChange={handleNumberChange}
          {...commonProps("volume")}
        />
      </div>
      <div data-testid="fsc-type-container" className="grid__cell one-half">
        <AutoComplete
          label={ messages.fscTypeOptional.defaultMessage }
          handleChange={value =>
            setModeDetails(mode.tl, "fuelSurchargeType", value)
          }
          staticOptions={fscTypes.filter(
            x => !x.division || x.division === userOrBidDivision
          )}
          {...commonProps("fuelSurchargeType")}
        />
      </div>
      {bidScac.value === bidScacType[0].value ? (
        <div data-testid="fsc-peg-container" className="grid__cell one-half">
          <OutlinedInput
            data-testid="fsc-peg"
            type="text"
            label={ messages.fscPegOptional.defaultMessage }
            pattern="^(\d{1}|\d{0,1}\.\d{0,2})$"
            min={userOrBidDivision === division.coy ? "0.01" : "0"}
            max="9.99"
            unit={getCurrency(userOrBidDivision)}
            isUnitPositionLeft={true}
            onBlur={e => {
              toggleFscFocused(false);
              handleValidation(e);
            }}
            onChange={handleNumberChange}
            {...commonProps("fuelSurchargePeg")}
            value={
              fscFocused
              ? modeDetails.fuelSurchargePeg
              : modeDetails.fuelSurchargePeg && !isNaN(modeDetails.fuelSurchargePeg)
              ? parseFloat(modeDetails.fuelSurchargePeg).toFixed(2)
              : null
            }
            onFocus={() => {
              !validationData.fuelSurchargePeg.isValid &&
                removeError("tl", "fuelSurchargePeg");
              toggleFscFocused(true);
            }}
          >
            {userOrBidDivision === division.coy &&
              (fscPegSpinner || (
                <button onClick={getCoyoteFsc} className="fsc-peg-helper">
                  {messages.useCoyoteFsc.defaultMessage}
                </button>
              ))}
            {fscPegSpinner && (
              <Spinner
                className="spinner fsc-peg-helper"
                height="16px"
                width="16px"
                fill="#0088D1"
              />
            )}
          </OutlinedInput>
        </div>
      ) : null}
      {bidScac.value === bidScacType[1].value ? (
        <>
          <div className="grid__cell one-half">
            <OutlinedInput
              id="rxoFuel"
              label={messages.rxoFuel.defaultMessage}
              pattern="^(\d{1}|\d{0,1}\.\d{0,2})$"
              min={userOrBidDivision === division.coy ? "0.01" : "0"}
              max="9.99"
              unit={getCurrency(userOrBidDivision)}
              isUnitPositionLeft={true}
              onBlur={handleValidation}
              onChange={handleNumberChange}
              {...commonProps("rxoFuel")}
            />
          </div>
          <div className="grid__cell one-half">
            <OutlinedInput
              id="customerFuel"
              label={messages.customerFuel.defaultMessage}
              pattern="^(\d{1}|\d{0,1}\.\d{0,2})$"
              min={userOrBidDivision === division.coy ? "0.01" : "0"}
              max="9.99"
              unit={getCurrency(userOrBidDivision)}
              isUnitPositionLeft={true}
              onBlur={handleValidation}
              onChange={handleNumberChange}
              {...commonProps("customerFuel")}
            />
          </div>
          <div className="grid__cell one-half pos-relative">
            <CustomDatePicker
              id="customerBranchAndNetworkTimeframeStartDate"
              label={
                messages.customerBranchAndNetworkTimeframeFrom.defaultMessage
              }
              showMonthYearPicker={true}
              dateFormat="MM/yyyy"
              minDate={twoYearOldDate}
              maxDate={
                modeDetails["customerBranchAndNetworkTimeframeEndDate"]
                  ? parseISO(
                      modeDetails["customerBranchAndNetworkTimeframeEndDate"]
                    )
                  : today
              }
              onChange={date =>
                setModeDetails(
                  mode.tl,
                  "customerBranchAndNetworkTimeframeStartDate",
                  date
                )
              }
              customInput={<InputMask type="text" mask="99/9999" />}
              {...commonProps("customerBranchAndNetworkTimeframeStartDate")}
            />
          </div>
          <div className="grid__cell one-half pos-relative">
            <CustomDatePicker
              id="customerBranchAndNetworkTimeframeEndDate"
              label={
                messages.customerBranchAndNetworkTimeframeTo.defaultMessage
              }
              showMonthYearPicker={true}
              dateFormat="MM/yyyy"
              minDate={
                modeDetails["customerBranchAndNetworkTimeframeStartDate"]
                  ? parseISO(
                      modeDetails["customerBranchAndNetworkTimeframeStartDate"]
                    )
                  : twoYearOldDate
              }
              maxDate={today}
              onChange={date =>
                setModeDetails(
                  mode.tl,
                  "customerBranchAndNetworkTimeframeEndDate",
                  date
                )
              }
              customInput={<InputMask type="text" mask="99/9999" />}
              {...commonProps("customerBranchAndNetworkTimeframeEndDate")}
            />
          </div>
        </>
      ) : null}
      <div data-testid="equipment-container" className="grid__cell one-half">
        <AutoComplete
          label={bidScac.value === bidScacType[1].value ? messages.solution.defaultMessage : messages.equipment.defaultMessage}
          handleChange={value =>
            setModeDetails(mode.tl, "equipmentTypes", value)
          }
          staticOptions={equipmentTypes.filter(
            x => !x.division || x.division === userOrBidDivision
          )}
          {...commonProps("equipmentTypes")}
          isMulti
        />
      </div>
      <div data-testid="currency-container" className="grid__cell one-half">
        <AutoComplete
          label={bidScac.value === bidScacType[1].value ? messages.opportunityCurrency.defaultMessage : messages.currency.defaultMessage}
          handleChange={handleCurrencyChange}
          staticOptions={currencyTypes}
          {...commonProps("currencyType")}
        />
      </div>
      <div
        data-testid="additional-currencies-container"
        className="grid__cell one-half"
      >
        <AutoComplete
          label={messages.additionalCurrenciesOptional.defaultMessage}
          value={modeDetails.additionalCurrencies}
          isMulti
          handleChange={value =>
            setModeDetails(mode.tl, "additionalCurrencies", value)
          }
          staticOptions={additionalCurrenciesOption}
        />
      </div>
      {userOrBidDivision === division.coy && (
        <div
          data-testid="international-lane-container"
          className="grid__cell one-half"
        >
          <AutoComplete
            label={bidScac.value === bidScacType[1].value ? messages.crossBorderOptional.defaultMessage : messages.internationalLanesOptional.defaultMessage}
            value={
              bidScac.value === bidScacType[1].value
                ? modeDetails.internationalLanes?.[0] ?? null
                : modeDetails.internationalLanes
            }
            isMulti={bidScac.value !== bidScacType[1].value}
            staticOptions={internationalLanesTypes.map(i => ({
              ...i,
              disabled: modeDetails.internationalLanes?.some(
                ({ value, disabled }) => i.value === value && disabled
              )
            }))}
            handleChange={selectedOptions =>
              setModeDetails(
                mode.tl,
                "internationalLanes",
                bidScac.value === bidScacType[1].value
                  ? selectedOptions
                    ? [selectedOptions]
                    : null
                  : selectedOptions
              )
            }
            {...(bidScac.value === bidScacType[1].value
              ? commonProps("internationalLanes")
              : {})}
          />
        </div>
      )}
      <div className="grid__cell one-half mb-24">
        <OutlinedInput
          label={bidScac.value === bidScacType[1].value ? messages.bidDetailsOptional.defaultMessage : messages.additionalInformationOptional.defaultMessage}
          maxLength="3000"
          onChange={event =>
            setModeDetails(mode.tl, "notes", event.target.value)
          }
          isMulti
          value={modeDetails.notes}
          onBlur={() =>
            setModeDetails(
              mode.tl,
              "notes",
              modeDetails.notes ? modeDetails.notes.trim() : ""
            )
          }
          dynamicHeight
        />
      </div>
      {isBidView && bidScac?.value === bidScacType[1].value && (
        <>
          <div className="grid__cell one-half">
            <AutoComplete
              id="bidSubmissionType"
              value={
                bidSubmissionTypes.find(
                  x => x.value === modeDetails.bidSubmissionType
                ) || bidSubmissionTypes[0]
              }
              label={messages.bidSubmissionType.defaultMessage}
              isClearable={false}
              isDisabled={!isSelfPriced}
              handleChange={
                isSelfPriced
                  ? value =>
                      setModeDetails(
                        mode.tl,
                        "bidSubmissionType",
                        value?.value ?? 0
                      )
                  : undefined
              }
              staticOptions={bidSubmissionTypes}
            />
          </div>
          {modeDetails.bidSubmissionType === 2 && (
            <div className="grid__cell one-whole">
              <OutlinedInput
                id="bidSubmissionDetails"
                name="bidSubmissionDetails"
                value={modeDetails.bidSubmissionDetails || ""}
                label={messages.bidSubmissionDetails.defaultMessage}
                maxLength={3000}
                isMulti={true}
                rows={6}
                disabled={!isSelfPriced}
                onChange={
                  isSelfPriced
                    ? event =>
                        setModeDetails(
                          mode.tl,
                          "bidSubmissionDetails",
                          event.target.value
                        )
                    : undefined
                }
                onBlur={isSelfPriced ? handleTrimText : undefined}
                dynamicHeight={isSelfPriced}
              />
            </div>
          )}
          {modeDetails.bidSubmissionType === 1 && (
            <div className="grid__cell one-whole">
              <OutlinedInput
                id="bidLoginDetails"
                name="bidLoginDetails"
                value={modeDetails.bidLoginDetails || ""}
                label={messages.bidLoginDetails.defaultMessage}
                maxLength={3000}
                isMulti={true}
                rows={6}
                disabled={!isSelfPriced}
                onChange={
                  isSelfPriced
                    ? event =>
                        setModeDetails(
                          mode.tl,
                          "bidLoginDetails",
                          event.target.value
                        )
                    : undefined
                }
                onBlur={isSelfPriced ? handleTrimText : undefined}
                dynamicHeight={isSelfPriced}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Truckload;
